/**
 * Removes nullish, empty, non-id and duplicate game ids while preserving order.
 *
 * The `getGames` endpoint builds its request path as `games/${ids.join(',')}`.
 * When a saved id list contains a `null`/`undefined`/empty entry (for example a
 * Slack channel whose preferences were persisted with a stray gameId), `join(',')`
 * collapses it into an empty path segment (`games/id1,,id2`). The apiserver treats
 * that empty segment as an invalid ObjectId and responds with HTTP 500, which blanks
 * the entire games list even though the remaining ids are valid (GR-2987).
 *
 * Filtering the list before building the path keeps a single bad entry from taking
 * down the whole request. Numbers are kept as strings so bulk list callers that use
 * numeric app ids still work.
 *
 * Callers must also tolerate a missing list: Slack preferences with no selected
 * games persist `gameIds: null`. Iterating that value throws and blanks the
 * Preferences page (GR-3085). A default parameter only covers `undefined`, so
 * treat any non-array the same as an empty list.
 */
export const sanitizeGameIds = (ids?: (string | number | null | undefined)[] | null): string[] => {
  if (!Array.isArray(ids)) {
    return []
  }

  const seen = new Set<string>()
  const sanitized: string[] = []

  for (const id of ids) {
    if (typeof id !== 'string' && typeof id !== 'number') continue
    const trimmed = String(id).trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    sanitized.push(trimmed)
  }

  return sanitized
}
